import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"

import { TenantSelect } from "@/components/admin/tenant-select"
import { PageHeader } from "@/components/page-header"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuditLogPages, useTenants } from "@/lib/queries"

/** Date inputs give a day; the upper bound has to include that whole day. */
function tagesende(datum: string): string {
  return new Date(`${datum}T23:59:59.999`).toISOString()
}

function tagesanfang(datum: string): string {
  return new Date(`${datum}T00:00:00`).toISOString()
}

function AdminProtokoll() {
  const [mandant, setMandant] = useState<string>()
  const [aktion, setAktion] = useState("")
  const [von, setVon] = useState("")
  const [bis, setBis] = useState("")
  const { data: tenants = [] } = useTenants()
  const log = useAuditLogPages({
    tenantId: mandant,
    action: aktion.trim() || undefined,
    von: von ? tagesanfang(von) : undefined,
    bis: bis ? tagesende(bis) : undefined,
  })
  const entries = log.data?.pages.flatMap((p) => p.entries) ?? []
  const tenantName = (id?: string) =>
    id ? (tenants.find((t) => t.id === id)?.name ?? id) : "Plattform"

  return (
    <div className="space-y-7">
      <PageHeader
        title="Protokoll"
        description="Wer wann was in der Verwaltung geändert hat, über alle Mandanten."
      />

      <Card className="gap-0 overflow-hidden p-0">
        <div className="grid gap-3 border-b border-[var(--rule)] bg-[var(--paper)]/45 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label>Mandant</Label>
            <TenantSelect
              value={mandant}
              onChange={setMandant}
              includeInternal
              allowAll
              className="w-full"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="protokoll-aktion">Aktion</Label>
            <Input
              id="protokoll-aktion"
              value={aktion}
              onChange={(e) => setAktion(e.target.value)}
              placeholder="z. B. gesperrt, Rolle, Export"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="protokoll-von">Von</Label>
            <Input
              id="protokoll-von"
              type="date"
              value={von}
              onChange={(e) => setVon(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="protokoll-bis">Bis</Label>
            <Input
              id="protokoll-bis"
              type="date"
              value={bis}
              onChange={(e) => setBis(e.target.value)}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zeitpunkt</TableHead>
                <TableHead>Mandant</TableHead>
                <TableHead>Akteur</TableHead>
                <TableHead>Aktion</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="font-mono text-[12.5px] whitespace-nowrap text-muted-foreground">
                    {new Date(entry.at).toLocaleString("de-DE", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </TableCell>
                  <TableCell className="text-[13.5px]">{tenantName(entry.tenantId)}</TableCell>
                  <TableCell className="text-[13.5px]">{entry.actor}</TableCell>
                  <TableCell className="text-[13.5px] font-medium">{entry.action}</TableCell>
                  <TableCell className="text-[13.5px] text-muted-foreground">
                    {entry.detail ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
              {!log.isLoading && entries.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="py-8 text-center text-[13.5px] text-muted-foreground"
                  >
                    Keine Einträge für diese Auswahl.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {log.hasNextPage && (
          <div className="flex justify-center border-t border-[var(--rule)] p-4">
            <Button
              variant="outline"
              onClick={() => void log.fetchNextPage()}
              disabled={log.isFetchingNextPage}
            >
              Ältere Einträge laden
            </Button>
          </div>
        )}
      </Card>
    </div>
  )
}

export const Route = createFileRoute("/_app/admin/protokoll")({
  component: AdminProtokoll,
})
